import React from "react";
import { Rate, Progress, Typography, Space, Divider } from "antd";
import { StarFilled, StarOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;

/**
 * Interface pour la distribution des notes (1-5 étoiles)
 */
interface RatingDistribution {
  5: number;
  4: number;
  3: number;
  2: number;
  1: number;
}

/**
 * Props du composant ProductRating
 */
interface ProductRatingProps {
  /** Note moyenne du produit (0.00 - 5.00) */
  rating: number;

  /** Nombre total d'avis */
  reviewsCount: number;

  /** Distribution des notes par étoiles (optionnel) */
  distribution?: RatingDistribution;

  /** Afficher le graphique de distribution (défaut: true) */
  showDistribution?: boolean;

  /** Afficher le lien "Voir tous les avis" (défaut: false) */
  showViewAllLink?: boolean;

  /** Callback lors du clic sur "Voir tous les avis" */
  onViewAllReviews?: () => void;

  /** Taille du composant ('small' | 'medium' | 'large') */
  size?: "small" | "medium" | "large";

  /** Mode compact (affichage inline pour cartes produits) */
  compact?: boolean;
}

/**
 * Composant ProductRating
 *
 * Affiche la note moyenne d'un produit avec distribution des étoiles
 *
 * @example
 * // Utilisation dans une carte produit (mode compact)
 * <ProductRating
 *   rating={4.5}
 *   reviewsCount={127}
 *   compact
 *   size="small"
 * />
 *
 * @example
 * // Utilisation dans une fiche produit détaillée
 * <ProductRating
 *   rating={4.5}
 *   reviewsCount={127}
 *   distribution={{5: 80, 4: 30, 3: 10, 2: 5, 1: 2}}
 *   showDistribution
 *   showViewAllLink
 *   onViewAllReviews={() => scrollToReviews()}
 *   size="large"
 * />
 */
const ProductRating: React.FC<ProductRatingProps> = ({
  rating,
  reviewsCount,
  distribution,
  showDistribution = true,
  showViewAllLink = false,
  onViewAllReviews,
  size = "medium",
  compact = false,
}) => {
  /**
   * Calcule le pourcentage d'avis pour une note donnée
   */
  const getPercentage = (starCount: number): number => {
    if (!distribution || reviewsCount === 0) return 0;
    const count = distribution[starCount as keyof RatingDistribution] || 0;
    return Math.round((count / reviewsCount) * 100);
  };

  /**
   * Formatte la note pour affichage (1 décimale si nécessaire)
   */
  const formatRating = (value: number): string => {
    return value % 1 === 0 ? value.toFixed(0) : value.toFixed(1);
  };

  /**
   * Tailles des étoiles selon le mode
   */
  const starSizes = {
    small: 16,
    medium: 20,
    large: 24,
  };

  /**
   * Tailles du texte selon le mode
   */
  const textSizes = {
    small: { rating: 16, count: 14 },
    medium: { rating: 20, count: 14 },
    large: { rating: 28, count: 16 },
  };

  /**
   * Rendu MODE COMPACT (cartes produits, catalogues)
   */
  if (compact) {
    return (
      <Space
        size={4}
        align="center"
        style={{
          display: "inline-flex",
          cursor: onViewAllReviews ? "pointer" : "default",
        }}
        onClick={onViewAllReviews}
      >
        <Rate
          disabled
          allowHalf
          value={rating}
          style={{ fontSize: starSizes[size], color: "#faad14" }}
        />
        <Text
          style={{
            fontSize: textSizes[size].count,
            color: "#595959",
            marginLeft: 4,
          }}
        >
          {formatRating(rating)}
          <span style={{ color: "#8c8c8c", marginLeft: 4 }}>
            ({reviewsCount})
          </span>
        </Text>
      </Space>
    );
  }

  /**
   * Rendu MODE COMPLET (fiche produit détaillée)
   */
  return (
    <div
      style={{
        padding: size === "large" ? "20px" : "12px",
        backgroundColor: "#fafafa",
        borderRadius: 8,
        border: "1px solid #f0f0f0",
      }}
    >
      {/* En-tête : Note moyenne + Étoiles */}
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Space align="center" size={12}>
          <Title
            level={size === "large" ? 2 : 3}
            style={{
              margin: 0,
              fontSize: textSizes[size].rating,
              fontWeight: 700,
            }}
          >
            {formatRating(rating)}
          </Title>
          <Space direction="vertical" size={0}>
            <Rate
              disabled
              allowHalf
              value={rating}
              style={{ fontSize: starSizes[size], color: "#faad14" }}
            />
            <Text
              style={{
                fontSize: textSizes[size].count,
                color: "#8c8c8c",
              }}
            >
              {reviewsCount} avis client{reviewsCount > 1 ? "s" : ""}
            </Text>
          </Space>
        </Space>

        {/* Graphique de distribution des notes */}
        {showDistribution && distribution && reviewsCount > 0 && (
          <>
            <Divider style={{ margin: "12px 0" }} />
            <Space
              direction="vertical"
              size={6}
              style={{ width: "100%", marginTop: 8 }}
            >
              {[5, 4, 3, 2, 1].map((stars) => {
                const percentage = getPercentage(stars);
                const count =
                  distribution[stars as keyof RatingDistribution] || 0;

                return (
                  <Space
                    key={stars}
                    size={8}
                    align="center"
                    style={{ width: "100%" }}
                  >
                    {/* Étoiles */}
                    <Space size={2} align="center" style={{ minWidth: 80 }}>
                      <Text
                        style={{
                          fontSize: 12,
                          color: "#595959",
                          fontWeight: 500,
                          minWidth: 12,
                        }}
                      >
                        {stars}
                      </Text>
                      <StarFilled
                        style={{ fontSize: 12, color: "#faad14" }}
                      />
                    </Space>

                    {/* Barre de progression */}
                    <Progress
                      percent={percentage}
                      showInfo={false}
                      strokeColor="#faad14"
                      trailColor="#f0f0f0"
                      style={{ flex: 1, marginBottom: 0 }}
                      strokeWidth={8}
                    />

                    {/* Pourcentage + Nombre */}
                    <Text
                      style={{
                        fontSize: 12,
                        color: "#8c8c8c",
                        minWidth: 60,
                        textAlign: "right",
                      }}
                    >
                      {percentage}% ({count})
                    </Text>
                  </Space>
                );
              })}
            </Space>
          </>
        )}

        {/* Lien "Voir tous les avis" */}
        {showViewAllLink && onViewAllReviews && (
          <a
            onClick={onViewAllReviews}
            style={{
              fontSize: 14,
              color: "#1890ff",
              cursor: "pointer",
              marginTop: 8,
              display: "inline-block",
            }}
          >
            Voir tous les avis ({reviewsCount})
          </a>
        )}
      </Space>
    </div>
  );
};

export default ProductRating;
